import React, { useState } from "react";
import { Table, Button, Spinner, Alert } from "react-bootstrap";
import CollapsibleSection from "./CollapsibleSection";
import GstCalculatorModal from "./GstCalculatorModal";
import Config from "../../../_config/config";
import myAxios from "../../../_services/axiositeachWrapper";
import EnrollmentService from "../../../_services/EnrollmentService";
import { MONEY_DECIMALS } from "../../../config/money";

interface PlanDetailsSectionProps {
  planData: any;
  loading: boolean;
}

const PlanDetailsSection: React.FC<PlanDetailsSectionProps> = ({ planData, loading }) => {
  // Grouped plan data (one row per installment, fee heads as columns) — built
  // server-side so tax math stays authoritative.
  const planHeads: string[] = planData?.planHeads || [];
  const planRows: any[] = planData?.planRows || [];
  const installments = planData?.installments || [];
  const invoices = planData?.invoices || [];
  // Invoice UI (info block + invoice columns) only renders for invoice-based tenants
  const invoiceBased: boolean = !!planData?.invoiceBasedSystem;
  const [pdfLoading, setPdfLoading] = useState<Record<number, boolean>>({});
  const [showCalc, setShowCalc] = useState(false);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);

  const fmt = (n: any) =>
    n == null || n === ""
      ? "0"
      : Number(n).toLocaleString(undefined, { maximumFractionDigits: MONEY_DECIMALS });

  // Column totals for the plan table footer (Java parity: a Total row summing
  // each fee head + Plan / Discount / Paid / Balance / Balance after Tax).
  // Summed from the visible rows so the footer always matches the columns.
  const sumBy = (fn: (r: any) => any) =>
    planRows.reduce((s: number, r: any) => s + (Number(fn(r)) || 0), 0);
  const headTotals: Record<string, number> = {};
  for (const h of planHeads) {
    headTotals[h] = sumBy((r) => r.headAmounts?.[h]);
  }
  // Discount shown = the real applied discount (type-6 details) when present,
  // else the plan-record discount — so "(Plan - Discount) + GST" reads truthfully.
  const rowDiscount = (r: any) =>
    r.appliedDiscount && r.appliedDiscount > 0 ? r.appliedDiscount : r.discount;
  const totals = {
    plan: sumBy((r) => r.plan),
    discount: sumBy(rowDiscount),
    payableWithTax: sumBy((r) => r.payableWithTax),
    paid: sumBy((r) => r.paid),
    balance: sumBy((r) => r.balance),
    balanceAfterTax: sumBy((r) => r.balanceAfterTax),
  };
  // Excess amount — authoritative available excess/credit from the student
  // credit account (Java shows this in the plan footer). Falls back to the
  // plan overpayment (negative tax-inclusive balance) when not provided.
  const excessAmount =
    planData?.excessAmount != null
      ? Number(planData.excessAmount)
      : totals.balanceAfterTax < 0
        ? -totals.balanceAfterTax
        : 0;

  const handlePrintInvoice = async (invoiceId: number) => {
    setInvoiceError(null);
    // Open the window synchronously (before await) to avoid popup blockers.
    const printWin = window.open("", "_blank");
    try {
      const res = await (myAxios as any).get(
        `${Config.nodeSiteUrl}v1/enrollment/invoice/${invoiceId}/print`,
      );
      const html = typeof res.data === "string" ? res.data : res.data?.html || "";
      if (printWin) {
        printWin.document.write(html);
        printWin.document.close();
      }
    } catch (err: any) {
      console.error("Print invoice error:", err);
      printWin?.close();
      setInvoiceError("Failed to load invoice: " + (err.message || "Unknown error"));
    }
  };

  const handleGeneratePdf = async (invoiceId: number) => {
    setInvoiceError(null);
    setPdfLoading((prev) => ({ ...prev, [invoiceId]: true }));
    try {
      const res: any = await EnrollmentService.generateInvoicePdf(invoiceId);
      if (res.data?.success && res.data.data?.presignedUrl) {
        window.open(res.data.data.presignedUrl, "_blank");
      } else {
        setInvoiceError(res.data?.message || "Failed to generate PDF");
      }
    } catch (err: any) {
      setInvoiceError("Failed to generate PDF: " + (err.message || "Unknown error"));
    } finally {
      setPdfLoading((prev) => ({ ...prev, [invoiceId]: false }));
    }
  };

  return (
    <CollapsibleSection
      title="Plan Details"
      count={planRows.length || installments.length}
      headerActions={
        <Button
          variant="secondary"
          size="sm"
          className="ev-calc-btn"
          title="GST Calculator"
          onClick={() => setShowCalc(true)}
        >
          <i className="fa fa-calculator" />
        </Button>
      }
    >
      <GstCalculatorModal show={showCalc} onHide={() => setShowCalc(false)} />
      {loading ? (
        <div className="ev-loading"><i className="fa fa-spinner fa-spin" /> Loading plan...</div>
      ) : installments.length === 0 ? (
        <div className="ev-empty">No plan details</div>
      ) : (
        <>
          {invoiceError && (
            <Alert
              variant="danger"
              onClose={() => setInvoiceError(null)}
              dismissible
              style={{ fontSize: 12 }}
            >
              {invoiceError}
            </Alert>
          )}
          {/* Invoice info — only for invoice-based tenants */}
          {invoiceBased && invoices.length > 0 && (
            <div style={{ marginBottom: 8, fontSize: 12 }}>
              {invoices.map((inv: any) => (
                <div key={inv.id} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                  <span style={{ fontWeight: 600 }}>Invoice: {inv.invoiceNumber}</span>
                  <span>Date: {inv.invoiceDate}</span>
                  <span>Amount: {inv.amount?.toLocaleString()}</span>
                  <Button
                    variant="secondary"
                    size="sm"
                    style={{ fontSize: 10, padding: "1px 6px" }}
                    onClick={() => handlePrintInvoice(inv.id)}
                    title="Print Invoice"
                  >
                    <i className="fa fa-print" /> Print
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    style={{ fontSize: 10, padding: "1px 6px" }}
                    onClick={() => handleGeneratePdf(inv.id)}
                    disabled={pdfLoading[inv.id]}
                    title="Download PDF"
                  >
                    {pdfLoading[inv.id] ? (
                      <><Spinner role="status" size="sm" animation="border" /></>
                    ) : (
                      <><i className="fa fa-file-pdf-o" /> PDF</>
                    )}
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div style={{ overflowX: "auto" }}>
            <Table bordered hover size="sm" style={{ fontSize: 12, marginBottom: 8 }}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Installment</th>
                  {invoiceBased && <th>Invoice No.</th>}
                  {planHeads.map((h) => (
                    <th key={h} style={{ textAlign: "right" }}>{h}</th>
                  ))}
                  <th style={{ textAlign: "right" }}>Plan</th>
                  <th style={{ textAlign: "right" }}>Discount</th>
                  <th
                    style={{ textAlign: "right", whiteSpace: "nowrap" }}
                    title="Amount payable = (Plan − Discount) + Tax"
                  >
                    Payable (incl. Tax)
                  </th>
                  <th style={{ textAlign: "right" }}>Paid</th>
                  <th style={{ textAlign: "right" }}>Balance</th>
                  <th style={{ textAlign: "right" }}>Balance after Tax</th>
                </tr>
              </thead>
              <tbody>
                {planRows.map((r: any, idx: number) => (
                  <tr key={r.installmentId}>
                    <td>{r.dueDate || "--"}</td>
                    <td>{r.termName || `Installment ${idx + 1}`}</td>
                    {invoiceBased && <td>{r.invoiceNumber || "--"}</td>}
                    {planHeads.map((h) => (
                      <td key={h} style={{ textAlign: "right" }}>
                        {fmt(r.headAmounts?.[h])}
                      </td>
                    ))}
                    <td style={{ textAlign: "right" }}>{fmt(r.plan)}</td>
                    <td style={{ textAlign: "right" }}>{fmt(rowDiscount(r))}</td>
                    <td
                      style={{
                        textAlign: "right",
                        fontWeight: 700,
                        color: "var(--brand-primary)",
                      }}
                    >
                      {fmt(r.payableWithTax)}
                    </td>
                    <td style={{ textAlign: "right" }}>{fmt(r.paid)}</td>
                    <td style={{ textAlign: "right" }}>{fmt(r.balance)}</td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}>
                      {fmt(r.balanceAfterTax)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="ev-plan-total-row">
                  <td colSpan={invoiceBased ? 3 : 2}>Total</td>
                  {planHeads.map((h) => (
                    <td key={h} style={{ textAlign: "right" }}>
                      {fmt(headTotals[h])}
                    </td>
                  ))}
                  <td style={{ textAlign: "right" }}>{fmt(totals.plan)}</td>
                  <td style={{ textAlign: "right" }}>{fmt(totals.discount)}</td>
                  <td style={{ textAlign: "right", color: "var(--brand-primary)" }}>
                    {fmt(totals.payableWithTax)}
                  </td>
                  <td style={{ textAlign: "right" }}>{fmt(totals.paid)}</td>
                  <td style={{ textAlign: "right" }}>{fmt(totals.balance)}</td>
                  <td style={{ textAlign: "right" }}>
                    {fmt(totals.balanceAfterTax)}
                  </td>
                </tr>
              </tfoot>
            </Table>
          </div>

          {/* Excess amount (available excess/credit) — Java-style plan footer */}
          <div className="ev-plan-summary">
            <div className="ev-plan-summary-row ev-plan-summary-total">
              <span className="ev-plan-summary-label">Excess Amount</span>
              <span
                className="ev-plan-summary-value"
                style={{ color: excessAmount > 0 ? "var(--status-success)" : "var(--ev-text)" }}
              >
                {fmt(excessAmount)}
              </span>
            </div>
          </div>
        </>
      )}
    </CollapsibleSection>
  );
};

export default PlanDetailsSection;
